/**
 * WorkPane
 *
 * The bottom half of the stage: one pane, three tools. The learner works in the
 * shell, switches to the editor to fix a file, switches to the pull request to
 * read what a reviewer asked for, switches back. All three are the same
 * workspace.
 *
 * The rule that shapes this file: **no tab is ever unmounted**. Hiding is
 * done with the `hidden` attribute, never with `{tab === 'x' && ...}`. Removing
 * the terminal from the tree would detach it from the guest's serial line and
 * take the learner's shell — their history, their environment, the directory they had cd'd into,
 * all gone on a tab click. The editor has the same problem in miniature: an
 * unmounted buffer is an unsaved buffer thrown away. So all three live for as
 * long as the pane does, and only their visibility changes. The pull request
 * reader has nothing to lose by being unmounted, and is mounted anyway: one
 * rule for the pane is worth more than an exception nobody would remember.
 *
 * The editor is the one tool that is useless alone. Writing code and not
 * seeing it run is not how anybody works, so with the editor showing, the
 * terminal sits beside it — side by side is a toggle, on unless the learner
 * turns it off — and the editor's Run buttons type into it. The terminal
 * panel is the same mounted panel either way; side by side only stops hiding
 * it.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { WORKSPACE_ROOT } from '@engineer-os/vm';
import { TerminalPane, type TerminalHandle } from './TerminalPane';
import { EditorPane, type EditorHandle, type EditorStatus, type RunKind, type RunOutcome } from './EditorPane';
import { PullRequestPane } from './PullRequestPane';
import { track } from '../../lib/events';
import { commandLine, locate, promptDirectory } from '../../lib/run';
import { registerSpotTerminal } from '../../lib/spot-help';
import { sound } from '../../lib/sound';
import { Machine, Parts } from '../../ui';
import { useLocale, useStrings } from '../../state/useLocale';
import type { TerminalLocations } from '../../terminal';
import type { FileEntry, GitState, PublicMission } from '../../types';

export interface WorkPaneProps {
  sessionId: string;
  cwd: string;
  /** Which level this is, for the `editor-run` event and nothing else (docs/analytics.md). */
  mission: Pick<PublicMission, 'level' | 'track'>;
  onGitState(state: GitState): void;
  /** How the command that just finished ended; false when it exited non-zero. */
  onCommandResult?(ok?: boolean): void;
  reconnectKey: number;
  /**
   * Commands that ran in this workspace without passing through the
   * terminal: the ones Iris proposed and the learner approved. A counter,
   * and only a change matters, exactly like `reconnectKey`.
   */
  commandsElsewhere?: number;
  onSaved(path: string): void;
}

type Tab = 'terminal' | 'editor' | 'pulls';

/** Order only. The labels come from the catalogue at render. */
const TABS: readonly Tab[] = ['terminal', 'editor', 'pulls'];

/**
 * Which tool the learner had open, remembered across levels and reloads.
 * Someone who works in the editor should not have to reach for it on every
 * level, and the terminal is the right default for anyone who has never
 * opened the editor at all.
 */
const TAB_KEY = 'engineeros.workpane.tab';

function initialTab(): Tab {
  try {
    // Read against the list rather than against one name, so a tab stored by
    // a build that had one more of them lands on the terminal rather than on
    // a panel that is no longer there.
    const stored = localStorage.getItem(TAB_KEY);
    return TABS.find((t) => t === stored) ?? 'terminal';
  } catch {
    return 'terminal';
  }
}

/** Whether the terminal sits beside the editor. On until turned off. */
const SPLIT_KEY = 'engineeros.workpane.split';

/**
 * How much of the pane's width the editor takes when the terminal is beside
 * it, as a fraction, so a stored split survives a different window. The
 * bounds keep either side wide enough to be worth having: the editor carries
 * a file tree, and a terminal under about forty columns wraps every prompt.
 */
const SPLIT_AT_KEY = 'engineeros.workpane.splitAt';
const SPLIT_AT = { min: 0.35, max: 0.78, initial: 0.58, step: 0.02 };

const clampSplitAt = (v: number) => Math.min(SPLIT_AT.max, Math.max(SPLIT_AT.min, v));

function initialSplit(): boolean {
  try {
    return localStorage.getItem(SPLIT_KEY) !== '0';
  } catch {
    return true;
  }
}

function initialSplitAt(): number {
  try {
    const stored = Number.parseFloat(localStorage.getItem(SPLIT_AT_KEY) ?? '');
    return Number.isFinite(stored) ? clampSplitAt(stored) : SPLIT_AT.initial;
  } catch {
    return SPLIT_AT.initial;
  }
}

function remember(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode, or storage disabled */
  }
}

const percent = (fraction: number) => `${Math.round(fraction * 1000) / 10}%`;

export function WorkPane({ sessionId, cwd, mission, onGitState, onCommandResult, reconnectKey, commandsElsewhere = 0, onSaved }: WorkPaneProps) {
  const s = useStrings().play;
  const rtl = useLocale().dir === 'rtl';
  const [tab, setTab] = useState<Tab>(initialTab);
  const [termStatus, setTermStatus] = useState(s.work.connecting);
  const [editor, setEditor] = useState<EditorStatus>({ path: null, dirty: false });
  const [pullCount, setPullCount] = useState<number | null>(null);
  const [split, setSplit] = useState(initialSplit);
  const [splitAt, setSplitAt] = useState(initialSplitAt);
  const sideBySide = split && tab === 'editor';

  const termRef = useRef<TerminalHandle | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const tabEls = useRef(new Map<Tab, HTMLButtonElement>());

  // Read by `select` so a repeat selection is not a switch, without making the
  // callback depend on `tab` and churn every render.
  const tabRef = useRef<Tab>(tab);
  // Which of the two things wants focus after the swap: the revealed tool
  // (pointer or Enter) or the tab button itself (arrow-key roving).
  const wantsPane = useRef(false);
  const wantsTab = useRef(false);
  const mounted = useRef(false);

  const select = useCallback((next: Tab, intent: 'pane' | 'tab') => {
    if (tabRef.current === next) {
      if (intent === 'pane' && next === 'terminal') termRef.current?.focus();
      return;
    }
    tabRef.current = next;
    wantsPane.current = intent === 'pane';
    wantsTab.current = intent === 'tab';
    setTab(next);
    try {
      localStorage.setItem(TAB_KEY, next);
    } catch {
      /* private mode, or storage disabled */
    }
    sound.click();
  }, []);

  // Everything that has to happen *after* the revealed panel has a box again.
  // xterm cannot measure a `display:none` container, so it must be told to
  // refit here rather than at the click; the window event is what CodeMirror
  // and the fit addon both listen for.
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    window.dispatchEvent(new Event('resize'));
    if (tab === 'terminal') {
      termRef.current?.refit();
      if (wantsPane.current) termRef.current?.focus();
    }
    if (wantsTab.current) tabEls.current.get(tab)?.focus();
    wantsPane.current = false;
    wantsTab.current = false;
  }, [tab]);

  // Side by side on or off: both panels change width, and the terminal is
  // revealed or hidden, so both have to measure again once they have boxes.
  const splitMounted = useRef(false);
  useEffect(() => {
    if (!splitMounted.current) {
      splitMounted.current = true;
      return;
    }
    window.dispatchEvent(new Event('resize'));
    termRef.current?.refit();
  }, [sideBySide]);

  const toggleSplit = useCallback(() => {
    setSplit((on) => {
      remember(SPLIT_KEY, on ? '0' : '1');
      return !on;
    });
    sound.click();
  }, []);

  /* --- The divider ---------------------------------------------------------- */

  /**
   * The editor's share of the pane at a pointer position. The editor leads,
   * so it is on the left in English and on the right in Arabic, and the same
   * `clientX` — which is physical in both — is the other fraction of the
   * width there (docs/i18n.md §7).
   */
  const fractionAt = useCallback(
    (clientX: number): number | null => {
      const box = bodyRef.current?.getBoundingClientRect();
      if (!box || box.width <= 0) return null;
      return clampSplitAt((rtl ? box.right - clientX : clientX - box.left) / box.width);
    },
    [rtl],
  );

  const commitSplitAt = useCallback((value: number) => {
    const next = clampSplitAt(value);
    setSplitAt(next);
    remember(SPLIT_AT_KEY, String(next));
    window.dispatchEvent(new Event('resize'));
  }, []);

  const onSplitPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const handle = e.currentTarget;
      const body = bodyRef.current;
      if (!body) return;
      e.preventDefault();
      handle.setPointerCapture(e.pointerId);
      handle.classList.add('is-dragging');
      const prevCursor = document.body.style.cursor;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      let last: number | null = null;

      const move = (ev: PointerEvent) => {
        const at = fractionAt(ev.clientX);
        if (at === null) return;
        last = at;
        // Written straight to the DOM, as PlayFrame's handles are: a render
        // at pointer rate would re-render the terminal and the editor on
        // every frame.
        body.style.setProperty('--split-at', percent(at));
      };

      const up = () => {
        handle.classList.remove('is-dragging');
        document.body.style.cursor = prevCursor;
        document.body.style.userSelect = '';
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        window.removeEventListener('pointercancel', up);
        if (last !== null) commitSplitAt(last);
      };

      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
      window.addEventListener('pointercancel', up);
    },
    [fractionAt, commitSplitAt],
  );

  const onSplitKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Home') {
        e.preventDefault();
        commitSplitAt(SPLIT_AT.initial);
        return;
      }
      // Towards the terminal grows the editor, and the terminal is on the
      // other side in Arabic.
      const grow = rtl ? 'ArrowLeft' : 'ArrowRight';
      const shrink = rtl ? 'ArrowRight' : 'ArrowLeft';
      if (e.key !== grow && e.key !== shrink) return;
      e.preventDefault();
      commitSplitAt(splitAt + (e.key === grow ? SPLIT_AT.step : -SPLIT_AT.step));
    },
    [rtl, splitAt, commitSplitAt],
  );

  /* --- Running from the editor ---------------------------------------------- */

  const splitRef = useRef(split);
  useEffect(() => {
    splitRef.current = split;
  });

  /**
   * Type a command from the editor's Run buttons into the shell.
   *
   * The command is relative to the level's root, so a `cd` goes in front
   * when the prompt says the shell is somewhere else (lib/run.ts). Side by
   * side, the output is already in view and focus stays with the code. With
   * the editor alone there is nowhere to see it, so the terminal comes
   * forward: the learner chose that layout, and turning the split back on
   * for them would be overruling it.
   */
  const root = cwd ? `${WORKSPACE_ROOT}/${cwd}` : WORKSPACE_ROOT;

  // Read at the moment of the event rather than closed over, so neither
  // callback below is rebuilt when a language switch hands down a new object.
  const missionRef = useRef(mission);
  useEffect(() => {
    missionRef.current = mission;
  });
  // One word per use, from a closed list (docs/analytics.md, `editor-run`):
  // which button, whether the terminal refused it, or a location followed.
  const record = useCallback((detail: RunKind | 'busy' | 'trace') => {
    const { level, track: path } = missionRef.current;
    track('editor-run', { level, track: path, detail });
  }, []);

  const run = useCallback(
    async (command: string, kind: RunKind): Promise<RunOutcome> => {
      const term = termRef.current;
      if (!term) return 'detached';
      const outcome = term.type(commandLine(command, root, promptDirectory(term.cursorLine())));
      if (outcome === 'typed') record(kind);
      else if (outcome === 'busy') record('busy');
      if (outcome === 'typed' && !splitRef.current) select('terminal', 'pane');
      return outcome;
    },
    [root, select, record],
  );

  useEffect(()=>registerSpotTerminal(sessionId,{
    run(command){
      const term=termRef.current;if(!term)return 'detached';
      const outcome=term.type(commandLine(command,root,promptDirectory(term.cursorLine())));
      if(outcome==='typed'){select('terminal','pane');term.focus();}
      return outcome;
    },
    async target(){
      select('terminal','pane');
      await new Promise<void>(resolve=>requestAnimationFrame(()=>resolve()));
      termRef.current?.refit();
      return document.querySelector<HTMLElement>('#work-panel-terminal');
    },
  }),[sessionId,reconnectKey,root,select]);

  /* --- From the terminal back to the code ----------------------------------- */

  // Every file the tree last listed, which is what a relative path in the
  // output has to be one of before it is underlined (lib/run.ts, `locate`).
  const filesRef = useRef<ReadonlySet<string>>(new Set());
  const onEntries = useCallback((entries: readonly FileEntry[]) => {
    filesRef.current = new Set(entries.filter((e) => !e.dir).map((e) => e.path));
  }, []);

  const editorRef = useRef<EditorHandle | null>(null);

  /**
   * A `path:line` in the shell's output, clicked: the file opens in the
   * editor with the caret on that line. The editor comes forward; side by
   * side the terminal stays where it is beside it, which is the whole point
   * — the trace on one side, the line it names on the other.
   */
  const locations = useMemo<TerminalLocations>(
    () => ({
      resolve: (path) => locate(path, root, filesRef.current),
      open: (path, line, col) => {
        record('trace');
        select('editor', 'pane');
        void editorRef.current?.openAt(path, line, col ?? 1);
      },
    }),
    [root, select, record],
  );

  // WAI-ARIA's tablist keys are "previous" and "next", not "left" and
  // "right": on a mirrored page ArrowLeft moves forward through the tabs,
  // because forward is the direction the tabs are laid out in.
  const onTabKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const index = TABS.indexOf(tabRef.current);
      if (index === -1) return;
      const forward = rtl ? 'ArrowLeft' : 'ArrowRight';
      const back = rtl ? 'ArrowRight' : 'ArrowLeft';
      let next = index;
      if (event.key === forward || event.key === 'ArrowDown') next = (index + 1) % TABS.length;
      else if (event.key === back || event.key === 'ArrowUp') next = (index - 1 + TABS.length) % TABS.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = TABS.length - 1;
      else return;
      event.preventDefault();
      const target = TABS[next];
      if (target) select(target, 'tab');
    },
    [select, rtl],
  );

  const onTerminalStatus = useCallback((text: string) => {
    setTermStatus((prev) => (prev === text ? prev : text));
  }, []);

  const onEditorStatus = useCallback((status: EditorStatus) => {
    setEditor((prev) => (prev.path === status.path && prev.dirty === status.dirty ? prev : status));
  }, []);

  const onPullCount = useCallback((count: number | null) => {
    setPullCount((prev) => (prev === count ? prev : count));
  }, []);

  /**
   * A command finished.
   *
   * The terminal reads the git state once per prompt and hands it up; that
   * same moment is the only one in which a pull request can have changed,
   * because the only way to change one is to run a command. So the pull
   * request reader is refreshed off this rather than off a timer, and the
   * graph's signal is the pane's signal.
   *
   * The prop is read through a ref so that this callback is stable and the
   * terminal is never re-attached by a parent handing down a fresh closure.
   */
  const gitRef = useRef(onGitState);
  useEffect(() => {
    gitRef.current = onGitState;
  });
  const [commandTick, setCommandTick] = useState(0);
  const handleGitState = useCallback((state: GitState) => {
    gitRef.current(state);
    setCommandTick((n) => n + 1);
  }, []);

  // The open file's path is the machine's; the state beside it is a word.
  // The catalogue decides how the two are ordered and separated.
  const status =
    tab === 'terminal' ? (
      termStatus
    ) : tab === 'pulls' ? (
      pullCount === null ? (
        s.work.pullsReading
      ) : (
        s.work.pullCount(pullCount)
      )
    ) : editor.path ? (
      <Parts of={s.work.fileState(<Machine key="path">{editor.path}</Machine>, editor.dirty)} />
    ) : (
      s.work.noFile
    );

  const splitStyle: CSSProperties = { ['--split-at' as never]: percent(splitAt) as never };

  return (
    <section className={`pane pane-term work-pane${sideBySide ? ' is-split' : ''}`}>
      <div className="pane-head work-head">
        <div className="work-tabs" role="tablist" aria-label={s.work.tablist} onKeyDown={onTabKeyDown}>
          {TABS.map((id) => (
            <button
              key={id}
              type="button"
              ref={(el) => {
                if (el) tabEls.current.set(id, el);
                else tabEls.current.delete(id);
              }}
              className={`work-tab${tab === id ? ' is-on' : ''}`}
              role="tab"
              id={`work-tab-${id}`}
              aria-selected={tab === id}
              aria-controls={`work-panel-${id}`}
              tabIndex={tab === id ? 0 : -1}
              onClick={() => select(id, 'pane')}
            >
              {id === 'terminal' ? s.work.terminal : id === 'editor' ? s.work.editor : s.work.pulls}
            </button>
          ))}
        </div>

        <span className="note work-status">{status}</span>

        {/* A layout control rather than a fourth tab: it belongs to the
            editor, so it is only there while the editor is. */}
        {tab === 'editor' && (
          <button
            type="button"
            className={`work-split${split ? ' is-on' : ''}`}
            aria-pressed={split}
            title={s.work.splitTitle}
            onClick={toggleSplit}
          >
            <span className="work-split-mark" aria-hidden="true" />
            {s.work.split}
          </button>
        )}
      </div>

      <div className={`pane-body work-body${sideBySide ? ' is-split' : ''}`} ref={bodyRef} style={splitStyle}>
        {/* Mounted for the life of the pane. `hidden` only hides it: the socket
            stays open and the scrollback survives every switch. Side by side
            it is simply not hidden: the same terminal, the same shell.

            `dir="ltr"` no matter the language (docs/i18n.md §7). The guest is
            an English Linux drawing a character grid, and a terminal set
            right to left would put the prompt on the wrong side of its own
            output and reorder every path it printed. */}
        <div
          className="work-panel work-panel-terminal"
          role="tabpanel"
          id="work-panel-terminal"
          aria-labelledby="work-tab-terminal"
          dir="ltr"
          hidden={tab !== 'terminal' && !sideBySide}
        >
          <TerminalPane
            ref={termRef}
            sessionId={sessionId}
            onGitState={handleGitState}
            onStatus={onTerminalStatus}
            onCommandResult={onCommandResult}
            locations={locations}
            reconnectKey={reconnectKey}
          />
        </div>

        {/* Likewise: an unsaved buffer must survive a trip to the terminal. */}
        <div
          className="work-panel work-panel-editor"
          role="tabpanel"
          id="work-panel-editor"
          aria-labelledby="work-tab-editor"
          hidden={tab !== 'editor'}
        >
          <EditorPane
            sessionId={sessionId}
            cwd={cwd}
            onSaved={onSaved}
            onStatus={onEditorStatus}
            onRun={run}
            onEntries={onEntries}
            resetKey={reconnectKey}
            workspaceKey={commandTick + commandsElsewhere}
            ref={editorRef}
          />
        </div>

        {sideBySide && (
          <div
            className="rsz work-split-rsz"
            role="separator"
            aria-orientation="vertical"
            aria-label={s.work.resizeSplit}
            aria-valuemin={Math.round(SPLIT_AT.min * 100)}
            aria-valuemax={Math.round(SPLIT_AT.max * 100)}
            aria-valuenow={Math.round(splitAt * 100)}
            tabIndex={0}
            onPointerDown={onSplitPointerDown}
            onDoubleClick={() => commitSplitAt(SPLIT_AT.initial)}
            onKeyDown={onSplitKeyDown}
          />
        )}

        {/* And likewise, though this one has nothing to lose: it is mounted
            for the life of the pane because the pane has one rule.

            No `dir` of its own. This panel is prose about a repository — a
            title, a review, a comment somebody left — so its chrome follows
            the page, and the machine's own text inside it carries `dir="ltr"`
            where it sits (docs/i18n.md §7). */}
        <div
          className="work-panel"
          role="tabpanel"
          id="work-panel-pulls"
          aria-labelledby="work-tab-pulls"
          hidden={tab !== 'pulls'}
        >
          <PullRequestPane
            sessionId={sessionId}
            active={tab === 'pulls'}
            // All three are counters and only a change matters: one climbs
            // with every finished command, one with every command Iris ran,
            // the last with every workspace reset, and a reset is exactly as
            // good a reason to look again.
            refreshKey={commandTick + commandsElsewhere + reconnectKey}
            onCount={onPullCount}
          />
        </div>
      </div>
    </section>
  );
}

export default WorkPane;
